import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronLeft, RefreshCw, Loader2, AlertTriangle, Truck, ExternalLink, Globe2, Pencil } from 'lucide-react';
import { Button } from '../components/ui/button';
import axiosInstance from '../utils/axiosInstance';
import ShippyLoader from '../components/ShippyLoader';
import { eur, flag, Solution, Alternatives } from '../components/MobileSolution';
import ExperimentNotice from '../components/ExperimentNotice';

/* Phone-sized order lookup: recent orders, one order's recorded solution, and a
   re-simulation (same endpoints as the Packing Simulator, so the numbers cannot
   drift from the desktop page). Used from the bottom bar; works on desktop too. */

const when = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';

function StatusChip({ r }) {
  const cls = r.status === 'alert' ? 'bg-amber-100 text-amber-800' : r.status === 'error' ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800';
  return <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${cls}`}>{r.status}</span>;
}

function OrderRow({ r, onOpen }) {
  const diff = (r.amount_charged_shopify || 0) - (r.amount_calculated_optimal || 0);
  return (
    <button type="button" onClick={() => onOpen(r)} className="w-full text-left px-3 py-2.5 border-b border-border/60 active:bg-muted/60">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono font-semibold">{r.order_number}</span>
        <span className="text-[11px] text-muted-foreground">{when(r.created_at)}</span>
      </div>
      <div className="flex items-center justify-between gap-2 mt-0.5 text-xs">
        <span className="truncate">{flag(r.shipping_country)} {r.shipping_country} · {r.carrier_name || '–'}</span>
        <span className={`tabular-nums shrink-0 ${diff < 0 ? 'text-red-700' : 'text-emerald-700'}`}>{eur(r.amount_calculated_optimal)} <span className="text-muted-foreground">/ paid {eur(r.amount_charged_shopify)}</span></span>
      </div>
      <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
        <StatusChip r={r} />
        <span className="truncate">{r.shipping_solution || r.error_message || ''}</span>
      </div>
    </button>
  );
}

export default function MobileOrders() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [order, setOrder] = useState(null);          // live order (products, address)
  const [sim, setSim] = useState(null);              // re-simulation result
  const [simCountry, setSimCountry] = useState('');
  const [countries, setCountries] = useState([]);
  const [busy, setBusy] = useState('');
  const timer = useRef(null);

  const load = useCallback(async (search) => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('api/packing-analysis-records/', { params: { limit: 40, offset: 0, search: search || '' } });
      setList(r.data?.records || r.data?.results || []);
      setError('');
    } catch (e) {
      setError(`Could not load orders (${e?.response?.status || e.message}).`);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(''); }, [load]);
  useEffect(() => {
    axiosInstance.get('api/shipping-rates/').then(r => {
      const cs = r.data?.countries || r.data?.countries_data || [];
      setCountries(cs.map(c => c.name || c.country_name).filter(Boolean).sort());
    }).catch(() => {});
  }, []);
  const onSearch = (v) => {
    setQ(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => load(v.trim()), 350);
  };

  const open = async (r) => {
    setSelected(r); setOrder(null); setSim(null); setSimCountry(r.shipping_country || '');
  };
  const back = () => { setSelected(null); setOrder(null); setSim(null); };

  const resimulate = async (country) => {
    if (!selected) return;
    setBusy('sim');
    try {
      const num = selected.order_number.replace('#', '');
      let o = order;
      if (!o) {
        const g = await axiosInstance.get(`api/orders/${encodeURIComponent(num)}/`);
        o = g.data; setOrder(o);
      }
      const addr = o?.shipping_address || {};
      const dest = country || simCountry || addr.country || selected.shipping_country;
      const zip = country && country !== (addr.country || selected.shipping_country) ? '' : (addr.zip || addr.postal_code || '');
      const p = await axiosInstance.post(`api/orders/${encodeURIComponent(num)}/`, {
        products: o.products || [], shipping_country: dest, postal_code: zip,
        order_total: o.checkout_total_eur ?? undefined, address1: zip ? (addr.address1 || '') : '',
      });
      setSim({ country: dest, zip, solution: p.data?.solution, alternatives: p.data?.carrier_alternatives || null, pickup: p.data?.pickup, experiment: p.data?.shipping_experiment || null });
    } catch (e) {
      setSim({ error: e?.response?.data?.error || e?.response?.data?.message || e.message });
    } finally { setBusy(''); }
  };

  if (selected) {
    const r = selected;
    return (
      <div className="p-3 space-y-3 max-w-xl mx-auto">
        <button type="button" onClick={back} className="inline-flex items-center gap-1 text-sm text-primary"><ChevronLeft className="h-4 w-4" /> Orders</button>
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-xl font-semibold font-mono">{r.order_number}</div>
            <div className="text-xs text-muted-foreground">{flag(r.shipping_country)} {r.shipping_country} · {when(r.created_at)} · <StatusChip r={r} /></div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <button type="button" className="text-xs text-primary inline-flex items-center gap-1" onClick={() => navigate(`/m/simulate?order=${r.order_number.replace('#', '')}`)}>
              Edit cart <Pencil className="h-3 w-3" />
            </button>
            <button type="button" className="text-xs text-primary inline-flex items-center gap-1" onClick={() => navigate(`/packing-simulator?order=${r.order_number.replace('#', '')}`)}>
              Simulator <ExternalLink className="h-3 w-3" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 text-center">
          {[['Paid', r.amount_charged_shopify], ['Our cost', r.amount_calculated_optimal], ['Margin', r.gross_margin_percent !== null && r.gross_margin_percent !== undefined ? `${Number(r.gross_margin_percent).toFixed(1)}%` : '–']].map(([k, v]) => (
            <div key={k} className="rounded-lg border border-border bg-card py-2">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{k}</div>
              <div className="text-base font-semibold tabular-nums">{typeof v === 'number' ? eur(v) : v}</div>
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-border bg-card p-3 space-y-1 text-xs">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Recorded solution</div>
          <div>{r.shipping_solution || r.error_message || '–'}</div>
          <div className="text-muted-foreground flex items-center gap-1"><Truck className="h-3.5 w-3.5" /> {r.carrier_name || '–'} · {r.shipping_method}{r.surcharge_labels?.length ? ` · ${r.surcharge_labels.join(', ')}` : ''}</div>
          {r.slack_alert && <div className="text-amber-800 flex items-start gap-1"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> <span>{Array.isArray(r.slack_alert) ? r.slack_alert.join(' · ') : String(r.slack_alert)}</span></div>}
        </div>

        <div className="rounded-lg border border-border bg-card p-3 space-y-2">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Simulate again</div>
          <div className="flex items-center gap-2">
            <Globe2 className="h-4 w-4 text-muted-foreground shrink-0" />
            <input list="m-countries" value={simCountry} onChange={(e) => setSimCountry(e.target.value)} placeholder="Country" className="flex-1 rounded border border-border bg-background px-2 py-1.5 text-sm" />
            <datalist id="m-countries">{countries.map(c => <option key={c} value={c} />)}</datalist>
          </div>
          <Button className="w-full" disabled={busy === 'sim'} onClick={() => resimulate(simCountry)}>
            {busy === 'sim' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-1">{simCountry && simCountry !== r.shipping_country ? `What if shipped to ${simCountry}` : 'Re-simulate at today\'s rates'}</span>
          </Button>
          <div className="text-[11px] text-muted-foreground">Same engine and endpoints as the desktop simulator. A different country drops the postcode.</div>
        </div>

        {sim?.error && <div className="rounded border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-xs">{sim.error}</div>}
        {sim?.solution && <Solution sol={sim.solution} title={`Today, to ${sim.country}${sim.zip ? ` ${sim.zip}` : ''}`} />}
        {sim?.experiment && <ExperimentNotice block={sim.experiment} mode="simulator" compact />}
        {sim?.alternatives && <Alternatives alts={sim.alternatives} />}
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur px-3 pt-3 pb-2 border-b border-border/60">
        <h1 className="text-lg font-semibold mb-2">Orders</h1>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <input value={q} onChange={(e) => onSearch(e.target.value)} placeholder="Order number, country, carrier…" className="w-full rounded-md border border-border bg-background pl-8 pr-3 py-2 text-sm" inputMode="search" />
        </div>
      </div>
      {error && <div className="m-3 rounded border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>}
      {loading ? <div className="p-6"><ShippyLoader message="Loading orders" /></div> : (
        <div>
          {list.map(r => <OrderRow key={r.id} r={r} onOpen={open} />)}
          {list.length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">No orders match.</div>}
        </div>
      )}
    </div>
  );
}
